/**
 * Main platform detection API
 */

import type { EnvironmentInfo, EnvironmentType } from './types';
import {
  isCapacitor,
  isTelegramMiniApp,
  isPWA,
  isMobileDevice,
  getPlatform,
  getCapacitorInfo,
  getTelegramInfo,
  detectNativePlatform,
} from './detectors';

/**
 * Detect environment type with priority:
 * 1. Capacitor (highest priority - native app)
 * 2. Telegram Mini App
 * 3. PWA (installed web app)
 * 4. Mobile Web
 * 5. Desktop Web
 *
 * Uses native APIs from Capacitor and Telegram SDK for accurate detection
 */
export function detectEnvironment(): EnvironmentInfo {
  if (typeof window === 'undefined') {
    return {
      type: 'unknown',
      displayName: 'Unknown',
      isMobile: false,
      isPWA: false,
      isTelegram: false,
      isCapacitor: false,
      isNative: false,
      userAgent: '',
      platform: 'unknown',
      nativePlatform: 'unknown',
      standalone: false,
      screenWidth: 0,
      screenHeight: 0,
      pixelRatio: 1,
    };
  }

  // Gather info from native APIs
  const capacitorInfo = getCapacitorInfo();
  const telegramInfo = getTelegramInfo();

  // Basic detection
  const isCapacitorApp = isCapacitor();
  const isTelegramApp = isTelegramMiniApp();
  const isPWAApp = isPWA();
  const isMobile = isMobileDevice();
  const userAgent = navigator.userAgent;
  const platform = getPlatform();
  const standalone = isPWAApp;

  // Determine native platform
  const { platform: nativePlatform, isNative } = detectNativePlatform(
    capacitorInfo,
    telegramInfo,
    userAgent
  );

  // Determine environment type by priority
  let type: EnvironmentType;
  let displayName: string;

  if (isCapacitorApp && capacitorInfo) {
    type = 'capacitor';
    const platformName = capacitorInfo.platform.charAt(0).toUpperCase() + capacitorInfo.platform.slice(1);
    displayName = capacitorInfo.isNativePlatform
      ? `Native App (${platformName})`
      : 'Capacitor Web';
  } else if (isTelegramApp && telegramInfo) {
    type = 'telegram-mini-app';
    const platformMap: Record<string, string> = {
      ios: 'iOS',
      android: 'Android',
      android_x: 'Android X',
      macos: 'macOS',
      tdesktop: 'Desktop',
      weba: 'Web A',
      webk: 'Web K',
      web: 'Web',
    };
    const platformName = platformMap[telegramInfo.platform] || telegramInfo.platform;
    displayName = `Telegram Mini App (${platformName})`;
  } else if (isPWAApp) {
    type = 'pwa';
    displayName = 'Progressive Web App';
  } else if (isMobile) {
    type = 'mobile-web';
    displayName = 'Mobile Browser';
  } else {
    type = 'desktop-web';
    displayName = 'Desktop Browser';
  }

  // Screen info
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  const pixelRatio = window.devicePixelRatio || 1;

  return {
    type,
    displayName,
    isMobile,
    isPWA: isPWAApp,
    isTelegram: isTelegramApp,
    isCapacitor: isCapacitorApp,
    isNative,
    userAgent,
    platform,
    nativePlatform,
    standalone,
    capacitor: capacitorInfo || undefined,
    telegram: telegramInfo || undefined,
    screenWidth,
    screenHeight,
    pixelRatio,
  };
}
